// Standalone Qualifying Events admin surface. Renders the dashboard
// card list, the New Event modal, and the per-event detail page mounted
// at /admin/qualifying/:eventId. Lives outside /tournaments — the only
// data this surface touches is /qualifyingEvents.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { QRCodeCanvas } from 'qrcode.react'
import { get, ref, update } from 'firebase/database'

import AIAssistant from '../components/AIAssistant.jsx'
import TeamJoinCard from './TeamJoinCard.jsx'
import {
  approveAllTeams,
  assignQualifyingEventTokens,
  createQualifyingEvent,
  createQualifyingEventFromRegistration,
  deleteQualifyingEvent,
  setQualifyingEventStatus,
  setQualifyingEventLocked,
  submitQualifyingEventResult,
  watchQualifyingEvent,
  watchQualifyingEvents,
} from '../lib/qualifyingEvents.js'
import {
  bulkAddRegistrations,
  createTournament,
  updateTournamentInfo,
  watchTournaments,
} from '../lib/data.js'
import EventTitle, { splitEventTitle } from '../components/EventTitle.jsx'
import { db, tref, updateTenant } from '../lib/firebase.js'
import { FOURBALL_ONLY, PBCC_PREMIUM } from '../config/siteMode.js'
import { Modal, Field } from './AdminApp.jsx'
import { buildTournamentFromQualifying } from '../lib/buildTournament.js'
import { DEFAULT_PARS } from '../lib/scoring.js'
import CLUB from '../config/club.js'

const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'

// Format choices for the New Qualifying Event modal. PBCC 4-Ball first
// because that's the flagship event this whole flow was built for.
const QE_FORMATS = [
  '4-Ball Best Ball',
  'Stroke Play Individual',
  'Scramble',
  'Shamble',
  'Alternate Shot',
  'Stableford',
]

// ── Dashboard section ─────────────────────────────────────────────────
// Drops into AdminHome alongside My Tournaments / Demos. Shows a list
// of events (sorted newest-first) and a New Qualifying Event button.
export function QualifyingEventsSection() {
  const [events, setEvents] = useState({})
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    const off = watchQualifyingEvents(setEvents)
    return () => off()
  }, [])

  // The 4-Ball sidebar "New Qualifying Event" tab triggers the create flow here.
  useEffect(() => {
    const open = () => setCreating(true)
    window.addEventListener('pbcc4ball:new-qualifying', open)
    return () => window.removeEventListener('pbcc4ball:new-qualifying', open)
  }, [])

  const list = useMemo(() => {
    return Object.entries(events || {})
      .map(([id, v]) => ({
        id,
        name: v?.info?.name || '(unnamed)',
        date: v?.info?.date || null,
        endDate: v?.info?.endDate || null,
        format: v?.info?.format || '',
        status: v?.info?.status || 'open',
        builtTournamentId: v?.info?.builtTournamentId || null,
        createdAt: v?.info?.createdAt || 0,
        teamCount: v?.teams ? Object.keys(v.teams).length : 0,
        submittedCount: v?.teams
          ? Object.values(v.teams).filter((t) => t?.submittedAt).length
          : 0,
      }))
      // Once built into a tournament, the qualifying event has advanced —
      // drop it here so it only lives under My Tournaments (New Tournament).
      .filter((q) => !q.builtTournamentId)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
  }, [events])

  return (
    <section>
      <div className="mb-3">
        <div className="min-w-0">
          <span className="pbcc-gold-rule" aria-hidden="true" style={PBCC_PREMIUM ? { backgroundColor: 'var(--ge-gold)' } : FOURBALL_ONLY ? { backgroundColor: '#FFFFFF' } : undefined} />
          <h2 className="pbcc-h2 text-xl mt-1" style={(FOURBALL_ONLY || PBCC_PREMIUM) ? { color: '#FFFFFF' } : undefined}>Qualifying Events</h2>
          <p className={`text-xs mt-0.5 leading-snug ${FOURBALL_ONLY ? 'text-white' : 'text-gray-600'}`}>
            Lists sent here from Registration arrive already loaded, with name, dates, and teams inherited. Run qualifying, then <b>Approve &amp; Lock</b> the field — only then can it be built into a tournament.
          </p>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="pbcc-empty-card min-h-[220px]">
          <div
            className="hidden sm:flex w-52 md:w-64 lg:w-80 shrink-0 items-center justify-center relative"
            style={{
              backgroundImage: `url(/pbcc-flag.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'contrast(1.08) saturate(1.12)',
            }}>
          </div>
          <div className="px-5 py-6 sm:px-8 flex flex-col gap-1.5 justify-center">
            <h3 className="text-lg font-extrabold text-gray-900">No qualifying events yet.</h3>
            <p className="text-sm text-gray-600 leading-snug max-w-md">
              Use <b>New Qualifying Event</b> in the sidebar to create one. Players scan a QR code to enter their scores; you approve the field and create the tournament from there.
            </p>
          </div>
        </div>
      ) : (
        <div className={PBCC_PREMIUM ? 'pbcc-card-grid' : 'grid sm:grid-cols-2 lg:grid-cols-3 gap-4'}>
          {list.map((q) => <QualifyingEventCard key={q.id} q={q} />)}
        </div>
      )}

      {creating ? (
        <CreateEventModal onClose={() => setCreating(false)} />
      ) : null}
    </section>
  )
}

function QualifyingEventCard({ q }) {
  const created = q.createdAt ? new Date(q.createdAt).toLocaleDateString() : '—'
  return (
    <Link
      to={`/admin/qualifying/${q.id}`}
      className="block rounded-2xl p-4 transition active:scale-[0.99]"
      style={{
        backgroundColor: PBCC_PREMIUM ? 'var(--ge-cream-premium)' : '#FFFFFF',
        border: PBCC_PREMIUM ? '1.5px solid var(--ge-gold)' : `1px solid ${ACCENT}55`,
        boxShadow: PBCC_PREMIUM ? '0 10px 28px rgba(0,0,0,0.28)' : '0 6px 18px rgba(0,0,0,0.08)',
        textDecoration: 'none',
      }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {(() => {
            const { nameLines, dateLine } = splitEventTitle({ name: q.name, date: q.date, endDate: q.endDate })
            return (
              <>
                <h3 className="font-extrabold text-base" style={{ color: PRIMARY, lineHeight: 1.2 }}>
                  {(nameLines.length ? nameLines : [q.name]).map((l, i) => <div key={i}>{l}</div>)}
                </h3>
                {dateLine ? <p className="text-[11px] font-semibold mt-0.5" style={{ color: ACCENT }}>{dateLine}</p> : null}
              </>
            )
          })()}
        </div>
        <StatusBadge status={q.status} />
      </div>
      <p className="text-xs text-gray-600 mt-1">{q.format}</p>
      <div className="mt-3 flex items-baseline gap-3 text-xs" style={{ color: PRIMARY }}>
        <span><b>{q.submittedCount}</b> submitted</span>
        <span className="text-gray-500">·</span>
        <span><b>{q.teamCount}</b> total teams</span>
      </div>
      <p className="text-[11px] text-gray-500 mt-2">Created {created}</p>
    </Link>
  )
}

function StatusBadge({ status }) {
  const open = status !== 'closed'
  return (
    <span
      className="text-[10px] font-extrabold uppercase tracking-widest rounded-full px-2 py-0.5"
      style={PBCC_PREMIUM ? {
        backgroundColor: 'var(--ge-primary)',
        color: 'var(--ge-cream-premium)',
        border: '1px solid rgba(var(--ge-gold-rgb),0.75)',
      } : {
        backgroundColor: open ? 'rgba(34,168,90,0.12)' : 'rgba(0,0,0,0.06)',
        color: open ? '#15803D' : '#6B7280',
        border: `1px solid ${open ? '#15803D55' : '#6B728055'}`,
      }}>
      {open ? 'Open' : 'Closed'}
    </span>
  )
}

// New Qualifying Event = pick a registration that needs to qualify (mirrors New
// Tournament's BuildPicker). Picking one creates the linked qualifying event
// seeded from it (same result as Registration's "Send to Qualifying"). A small
// "fresh standalone round" fallback covers the rare no-source case.
function CreateEventModal({ onClose }) {
  const navigate = useNavigate()
  const [tournaments, setTournaments] = useState({})
  const [sel, setSel] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [fresh, setFresh] = useState(false)
  const [name, setName] = useState('PBCC 4-Ball 2026')
  const [format, setFormat] = useState(QE_FORMATS[0])

  useEffect(() => {
    const off = watchTournaments(setTournaments)
    return () => off()
  }, [])

  // Registrations marked "needs to qualify" that haven't been sent yet.
  const ready = useMemo(() => {
    const out = []
    for (const [id, v] of Object.entries(tournaments || {})) {
      if (!v || typeof v !== 'object') continue
      const info = v.info || {}
      if ((info.isDemo && !info.isPractice) || info.isTestStation) continue // practice field flows through
      if ((info.stage || 'registration') !== 'registration') continue
      if (info.status === 'closed') continue
      if (info.needsQualifying === false) continue // default ON; only explicit false skips
      if (info.qualifyingEventId) continue // already sent
      const count = v.registrations ? Object.keys(v.registrations).length : 0
      out.push({ id, info, count })
    }
    return out.sort((a, b) => (b.info.createdAt || 0) - (a.info.createdAt || 0))
  }, [tournaments])

  const selItem = ready.find((r) => r.id === sel) || null
  const firstLine = (n) => String(n || '(unnamed)').split('\n')[0]

  const onSend = async () => {
    if (!selItem) return
    setBusy(true); setErr('')
    try {
      const regs = tournaments?.[selItem.id]?.registrations || {}
      const eventId = await createQualifyingEventFromRegistration(selItem.id, selItem.info, regs)
      await updateTournamentInfo(selItem.id, { qualifyingEventId: eventId })
      onClose()
      navigate(`/admin/qualifying/${eventId}`)
    } catch (e) {
      setErr(e?.message || 'Could not send to Qualifying.')
      setBusy(false)
    }
  }

  const onCreateFresh = async () => {
    setBusy(true); setErr('')
    try {
      const eventId = await createQualifyingEvent({ name, format })
      onClose()
      navigate(`/admin/qualifying/${eventId}`)
    } catch (e) {
      setErr(e?.message || 'Could not create event.')
      setBusy(false)
    }
  }

  return (
    <Modal title="New Qualifying Event" onClose={onClose} size="lg">
      <div className="space-y-3">
        {!fresh ? (
          <>
            {ready.length > 0 ? (
              <>
                <p className="text-sm text-gray-700">
                  Pick a registration marked <b>needs to qualify</b>. Its name, dates, and teams carry over — no re-entry.
                </p>
                <select className="input" value={sel} onChange={(e) => setSel(e.target.value)} autoFocus>
                  <option value="">Choose a registration…</option>
                  {ready.map((r) => (
                    <option key={r.id} value={r.id}>
                      {firstLine(r.info.name)} — {r.count} team{r.count === 1 ? '' : 's'}
                    </option>
                  ))}
                </select>
                {selItem ? (
                  <div className="rounded-xl px-5 py-4" style={{ border: '1px solid rgba(var(--ge-primary-rgb),0.5)', background: '#FFFFFF' }}>
                    <EventTitle info={selItem.info} label="Send to Qualifying" />
                  </div>
                ) : null}
                <button type="button" className="btn-primary w-full" disabled={!sel || busy} onClick={onSend}>
                  {busy ? 'Sending…' : 'Send to Qualifying →'}
                </button>
                <div className="text-center text-xs text-gray-400">— or —</div>
              </>
            ) : (
              <p className="text-sm text-gray-600 leading-snug">
                No registrations are waiting to qualify. In <b>Registration</b>, turn on <b>“needs to qualify”</b> for a list — it then shows up here. Or start a fresh standalone round.
              </p>
            )}
            <button type="button" className="btn-secondary w-full" onClick={() => setFresh(true)}>
              Start a fresh standalone qualifying round
            </button>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-500 leading-snug">
              Standalone round — players self-register to qualify (no source registration).
            </p>
            <Field label="Event Name">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input" placeholder="e.g. PBCC 4-Ball 2026" autoFocus />
            </Field>
            <Field label="Format">
              <select value={format} onChange={(e) => setFormat(e.target.value)} className="input">
                {QE_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Field>
            <div className="flex gap-2 pt-1">
              <button type="button" className="btn-ghost text-sm" onClick={() => setFresh(false)} disabled={busy}>← Back</button>
              <button type="button" className="btn-primary text-sm flex-1" onClick={onCreateFresh} disabled={busy || !name.trim()}>
                {busy ? 'Creating…' : 'Create standalone round'}
              </button>
            </div>
          </>
        )}
        {err ? <p className="text-sm font-bold" style={{ color: '#B91C1C' }}>{err}</p> : null}
      </div>
    </Modal>
  )
}

// ── Detail page ───────────────────────────────────────────────────────
// Route: /admin/qualifying/:eventId. Shows the scan QR, the live team
// list (sorted by score asc, pending at bottom), and the admin
// controls — Approve All, Close, Create-Tournament.
// TEST: per-team qualify QR codes shown on screen (and printable) inside the
// qualifying event. Each QR encodes /play?e=<eventId>&tk=<token> so a player
// scans their team's code and drops straight into the real token-bound qualify
// flow — no pasted link.
function TeamQrCodesSection({ eventId, origin, teams, teamsMap }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [open, setOpen] = useState(true)
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(null)   // team object → its modal card
  const [printScope, setPrintScope] = useState('') // '' | 'one' | 'all'
  const withToken = teams.filter((t) => t.accessToken).length
  const missing = teams.length - withToken
  const teamLink = (tok) => `${origin}/play?e=${encodeURIComponent(eventId)}&tk=${encodeURIComponent(tok)}`
  const labelOf = (t) => [t.player1, t.player2].filter(Boolean).map((n) => String(n).trim().split(/\s+/).pop()).join(' / ') || t.teamName || t.id
  const linkOf = (t) => (t.accessToken ? teamLink(t.accessToken) : '')
  const generate = async () => {
    setBusy(true); setMsg('')
    try {
      const n = await assignQualifyingEventTokens(eventId, teamsMap)
      setMsg(n > 0 ? `Created ${n} new code${n === 1 ? '' : 's'}.` : 'Every team already has a code.')
    } catch (e) {
      setMsg(e?.message || 'Could not generate codes.')
    }
    setBusy(false)
  }
  // Auto-generate a code for every team that lacks one (idempotent; the ref
  // guards against re-firing while a generate is in flight).
  const autoGenRef = useRef(false)
  useEffect(() => {
    if (missing > 0 && !autoGenRef.current) {
      autoGenRef.current = true
      Promise.resolve(assignQualifyingEventTokens(eventId, teamsMap))
        .catch(() => {})
        .finally(() => { autoGenRef.current = false })
    }
  }, [missing, eventId]) // eslint-disable-line react-hooks/exhaustive-deps
  // Print scope decides WHICH cards print: 'all' = the hidden all-cards sheet,
  // 'one' = just the open modal card. Reset once the print dialog closes.
  useEffect(() => {
    const reset = () => setPrintScope('')
    window.addEventListener('afterprint', reset)
    return () => window.removeEventListener('afterprint', reset)
  }, [])
  const doPrint = (scope) => { setPrintScope(scope); setTimeout(() => { try { window.print() } catch { /* ignore */ } }, 120) }

  const q = query.trim().toLowerCase()
  const filtered = q
    ? teams.filter((t) => `${t.teamName || ''} ${t.player1 || ''} ${t.player2 || ''}`.toLowerCase().includes(q))
    : teams

  return (
    <div className="mt-6">
      <style>{'.tqp-all { display: none; }'}</style>
      {printScope ? (
        <style>{`@media print {
          body * { visibility: hidden !important; }
          .tqp[data-scope="all"] .tqp-all, .tqp[data-scope="all"] .tqp-all * { visibility: visible !important; }
          .tqp[data-scope="all"] .tqp-all { display: block !important; position: absolute; left: 0; top: 0; width: 100%; }
          .tqp[data-scope="one"] .tqp-modal-card, .tqp[data-scope="one"] .tqp-modal-card * { visibility: visible !important; }
          .tqp[data-scope="one"] .tqp-modal-card { position: absolute; left: 0; top: 0; width: 100%; display: flex !important; justify-content: center; }
          .team-join-card { page-break-after: always; break-after: page; }
          .team-join-card:last-child { page-break-after: auto; break-after: auto; }
        }`}</style>
      ) : null}
      <div className="tqp" data-scope={printScope}>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 no-print">
          <button type="button" onClick={() => setOpen((x) => !x)} className="font-extrabold text-base flex items-center gap-2" style={{ color: PRIMARY }} title={open ? 'Hide' : 'Show'}>
            <span>{open ? '▼' : '▶'}</span>
            🔑 Team Qualify QR Codes
            <span className="text-xs font-semibold text-gray-500">({teams.length})</span>
          </button>
          {open ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500">{withToken}/{teams.length} have a code</span>
              <button onClick={generate} disabled={busy} className="rounded-lg px-3 py-1.5 text-sm font-bold" style={{ background: PRIMARY, color: '#FFFFFF' }}>
                {busy ? 'Generating…' : 'Generate missing codes'}
              </button>
              <button onClick={() => doPrint('all')} disabled={withToken === 0} className="rounded-lg px-3 py-1.5 text-sm font-bold" style={{ background: ACCENT, color: PRIMARY }}>🖨 Print all</button>
            </div>
          ) : null}
        </div>

        {!open ? null : (<>
          {msg ? <p className="text-xs text-gray-600 mb-2 no-print">{msg}</p> : null}
          <p className="text-sm text-gray-600 mb-3 no-print">Search for a team and click it to open its printable STEP 1 / STEP 2 card, or use <b>Print all</b> to print every team’s card at once.</p>

          {teams.length === 0 ? (
            <p className="text-sm text-gray-600 italic no-print">No teams yet — teams appear here once the field is loaded.</p>
          ) : (
            <div className="no-print">
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search teams by name…"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-72 mb-2" />
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-500 italic px-1 py-2">No team matches “{query}”.</p>
              ) : (
                <ul className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                  {filtered.map((t) => (
                    <li key={t.id}>
                      <button type="button" onClick={() => setSelected(t)} className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center justify-between gap-3">
                        <span className="min-w-0">
                          <span className="font-bold" style={{ color: PRIMARY }}>{labelOf(t)}</span>
                          {(t.player1 || t.player2) ? <span className="text-xs text-gray-500 ml-2">{[t.player1, t.player2].filter(Boolean).join(' & ')}</span> : null}
                        </span>
                        <span className="text-xs font-semibold shrink-0" style={{ color: t.accessToken ? '#047857' : '#B45309' }}>{t.accessToken ? 'View card ›' : 'code pending…'}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Per-team modal — big STEP 1/STEP 2 card + Print this card */}
          {selected ? (
            <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 60, padding: 20, overflow: 'auto' }}>
              <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 24, marginBottom: 24 }}>
                <div className="tqp-modal-card">
                  {linkOf(selected)
                    ? <TeamJoinCard teamLabel={labelOf(selected)} step1Url={linkOf(selected)} step2Url={linkOf(selected)} />
                    : <div className="bg-white rounded-2xl p-8 text-center font-semibold" style={{ color: 'var(--ge-primary)' }}>Code is still generating for this team — try again in a moment.</div>}
                </div>
                <div className="no-print" style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'center' }}>
                  {linkOf(selected) ? <button onClick={() => doPrint('one')} className="rounded-lg px-4 py-2 text-sm font-bold" style={{ background: ACCENT, color: PRIMARY }}>🖨 Print this card</button> : null}
                  <button onClick={() => setSelected(null)} className="rounded-lg px-4 py-2 text-sm font-bold" style={{ background: '#fff', color: PRIMARY, border: `1px solid ${PRIMARY}` }}>Close</button>
                </div>
              </div>
            </div>
          ) : null}

          {/* Hidden all-cards sheet — printed only when Print all is used. */}
          <div className="tqp-all">
            {teams.filter((t) => t.accessToken).map((t) => (
              <TeamJoinCard key={t.id} teamLabel={labelOf(t)} step1Url={linkOf(t)} step2Url={linkOf(t)} />
            ))}
          </div>
        </>)}
      </div>
    </div>
  )
}

export function QualifyingEventDetailPage() {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [loaded, setLoaded] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [showCreateChoice, setShowCreateChoice] = useState(false)
  // Submit-to-Tournament opens the AI-vs-Manual choice modal (same two buttons
  // the New-Tournament promote flow uses) instead of silently building. The
  // modal owns its own busy state, so no local `building` flag is needed.
  const [showSubmitChoice, setShowSubmitChoice] = useState(false)
  // Delete access-code gate (in-app modal, not window.prompt).
  const [deleteGate, setDeleteGate] = useState(false)
  const [delCode, setDelCode] = useState('')
  const [delErr, setDelErr] = useState('')

  useEffect(() => {
    if (!eventId) return
    const off = watchQualifyingEvent(eventId, (v) => {
      setEvent(v)
      setLoaded(true)
    })
    return () => off()
  }, [eventId])

  if (!loaded) {
    return (
      <DetailShell>
        <p className="text-sm text-gray-600">Loading…</p>
      </DetailShell>
    )
  }
  if (!event) {
    return (
      <DetailShell>
        <p className="text-sm" style={{ color: '#B91C1C' }}>
          Qualifying event not found.
        </p>
        <Link to="/admin/pbcc-4ball" className="btn-primary mt-3 inline-block">← PBCC 4-Ball</Link>
      </DetailShell>
    )
  }

  const info = event.info || {}
  const teamsMap = event.teams || {}
  const teams = Object.entries(teamsMap).map(([id, t]) => ({ id, ...(t || {}) }))
  // Sort: submitted lowest-score first, pending teams to the bottom.
  teams.sort((a, b) => {
    const ap = !!a.submittedAt
    const bp = !!b.submittedAt
    if (ap !== bp) return ap ? -1 : 1
    if (ap && bp) return (a.score || 0) - (b.score || 0)
    return (a.createdAt || 0) - (b.createdAt || 0)
  })

  const closed = info.status === 'closed'
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const scanUrl = `${origin}/qualify?q=${eventId}`
  // Companion player-flow entry (PBCCGolfGames app wraps /play). This QR opens
  // the player straight into the Qualify screen, then the SAME device auto-
  // advances through Waiting → Assigned → Play as the field is released — no QR
  // needed again after this first scan.
  const playUrl = `${origin}/play?q=${eventId}`
  const submittedCount = teams.filter((t) => !!t.submittedAt).length

  // Approve & Lock: approve the WHOLE field (digital or paper) AND close the
  // round in one action, so an approved field is a locked field — the signal
  // New Tournament reads as "ready to build." Never gated on digital submissions.
  const onApproveAll = async () => {
    setBusy(true); setErr('')
    try {
      await approveAllTeams(eventId, teamsMap)
      await setQualifyingEventStatus(eventId, 'closed')
    }
    catch (e) { setErr(e?.message || 'Approve failed.') }
    finally { setBusy(false) }
  }

  const onToggleLock = async () => {
    try { await setQualifyingEventLocked(eventId, !info.locked) }
    catch (e) { setErr(e?.message || 'Lock toggle failed.') }
  }
  // Reset Approvals — drops approved:false and status:'submitted' on
  // every team so the admin can re-run the approve flow from scratch.
  // Useful during testing and when an approval was made in error.
  const onResetApprovals = async () => {
    if (!window.confirm('Reset every team back to "submitted" status? Approvals will be cleared.')) return
    setBusy(true); setErr('')
    try {
      const updates = {}
      for (const teamId of Object.keys(teamsMap || {})) {
        updates[`qualifyingEvents/${eventId}/teams/${teamId}/approved`] = false
        updates[`qualifyingEvents/${eventId}/teams/${teamId}/status`] = 'submitted'
      }
      if (Object.keys(updates).length > 0) {
        await updateTenant(updates)
      }
    } catch (e) {
      setErr(e?.message || 'Reset failed.')
    } finally {
      setBusy(false)
    }
  }
  const onClose = async () => {
    if (!window.confirm('Close qualifying? Players will no longer be able to submit scores.')) return
    setBusy(true); setErr('')
    try { await setQualifyingEventStatus(eventId, 'closed') }
    catch (e) { setErr(e?.message || 'Close failed.') }
    finally { setBusy(false) }
  }
  const onReopen = async () => {
    setBusy(true); setErr('')
    try { await setQualifyingEventStatus(eventId, 'open') }
    catch (e) { setErr(e?.message || 'Reopen failed.') }
    finally { setBusy(false) }
  }
  // Access-code-gated delete. The 4-digit code (1996) is entered in an IN-APP
  // modal (openDeleteGate → submitDeleteCode), NOT window.prompt — native
  // prompts can be suppressed by the browser and were easy to miss. Guards the
  // real 104-team qualifying event from an accidental delete: doDelete only runs
  // after the code checks out; Cancel or a wrong code deletes nothing.
  const openDeleteGate = () => { setDelCode(''); setDelErr(''); setDeleteGate(true) }
  const submitDeleteCode = () => {
    if (delCode.trim() !== '1996') { setDelErr('Incorrect access code — deletion cancelled.'); return }
    setDeleteGate(false)
    doDelete()
  }
  const doDelete = async () => {
    setBusy(true); setErr('')
    try {
      await deleteQualifyingEvent(eventId)
      navigate('/admin')
    } catch (e) {
      setErr(e?.message || 'Delete failed.')
      setBusy(false)
    }
  }

  return (
    <DetailShell>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-extrabold" style={{ color: ACCENT }}>
            Qualifying Event
          </p>
          {(() => {
            const { nameLines, dateLine } = splitEventTitle(info)
            return (
              <>
                <h1 className="text-2xl font-extrabold" style={{ color: PRIMARY, lineHeight: 1.2 }}>
                  {(nameLines.length ? nameLines : [info.name || 'Untitled']).map((l, i) => <div key={i}>{l}</div>)}
                </h1>
                {dateLine ? <p className="text-sm font-semibold mt-0.5" style={{ color: ACCENT }}>{dateLine}</p> : null}
              </>
            )
          })()}
          <p className="text-xs text-gray-600 mt-0.5">
            {info.format} · Created {info.createdAt ? new Date(info.createdAt).toLocaleDateString() : '—'}
          </p>
        </div>
        <StatusBadge status={info.status} />
      </div>

      {/* ★ COMPANION APP QR — the primary player entry point. Dark-green, at the
          TOP so it can't be missed; it's what players scan to enter the /play
          flow (Qualify → Waiting → Assigned → Play). */}
      <div className="mt-4 rounded-2xl p-5" style={{ backgroundColor: PRIMARY, color: '#FFFFFF' }}>
        <p className="text-sm uppercase tracking-widest font-extrabold mb-3">📱 Companion App — Player QR</p>
        <div className="grid sm:grid-cols-[auto_1fr] gap-5 items-center">
          <div className="rounded-xl bg-white p-3 flex justify-center">
            <QRCodeCanvas value={playUrl} size={190} includeMargin />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-white/90 leading-snug">
              Players scan this to enter the app. It opens straight into <b>Qualify</b>, then auto-advances to their
              match assignment and scorecard as the field is released. Both players can scan — the second taps
              <b> “Join your team.”</b>
            </p>
            <p className="text-xs font-mono mt-2 break-all text-white/70">{playUrl}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => window.print()}
                className="rounded-lg px-3.5 py-2 text-sm font-bold transition active:scale-[0.98]"
                style={{ background: '#FFFFFF', color: PRIMARY }}>
                🖨 Print QR
              </button>
              <a
                href={playUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-lg px-3.5 py-2 text-sm font-bold transition active:scale-[0.98]"
                style={{ background: 'rgba(255,255,255,0.14)', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.4)' }}>
                Open in new tab
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* QR + scan URL block. Always render so the admin can print the QR
          on day-of even after closing — they may want to display the
          closed-message screen to players who arrive late. */}
      <div
        className="mt-4 rounded-2xl p-4 grid sm:grid-cols-[auto_1fr] gap-4 items-center"
        style={{ backgroundColor: '#FFFFFF', border: `1px solid ${ACCENT}55` }}>
        <div className="flex justify-center">
          <QRCodeCanvas value={scanUrl} size={160} includeMargin />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest font-extrabold" style={{ color: ACCENT }}>
            Scan to play qualifying
          </p>
          <p className="text-sm font-mono mt-1 break-all" style={{ color: PRIMARY }}>{scanUrl}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="btn-secondary text-sm">
              🖨 Print QR
            </button>
            <a
              href={scanUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-ghost text-sm">
              Open in new tab
            </a>
          </div>
        </div>
      </div>


      {/* Admin actions row */}
      <div className="mt-4 flex flex-wrap gap-2">
        {!closed ? (
          <>
            <button
              type="button"
              onClick={onApproveAll}
              disabled={busy}
              className="btn-primary text-sm">
              ✅ Approve &amp; Lock Field
            </button>
            <button
              type="button"
              onClick={onResetApprovals}
              disabled={busy || teams.length === 0}
              className="btn-secondary text-sm">
              ↺ Reset Approvals
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="btn-secondary text-sm">
              🔒 Close Qualifying
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onReopen}
              disabled={busy}
              className="btn-secondary text-sm">
              🔓 Re-open Qualifying
            </button>
            {info.sourceRegistrationId ? (
              <button
                type="button"
                onClick={() => setShowSubmitChoice(true)}
                disabled={busy}
                className="btn-primary text-sm">
                🏆 Submit to Tournament
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowCreateChoice(true)}
                className="btn-primary text-sm">
                ▶ Create Tournament from This List
              </button>
            )}
          </>
        )}
        <button
          type="button"
          onClick={onToggleLock}
          disabled={busy}
          className="btn-secondary text-sm"
          style={{ marginLeft: 'auto' }}
          title={info.locked ? 'Locked — delete needs the access code' : 'Lock to guard against accidental delete'}>
          {info.locked ? '🔒 Locked' : '🔓 Lock'}
        </button>
        <button
          type="button"
          onClick={openDeleteGate}
          disabled={busy}
          className="btn-ghost text-sm"
          style={{ color: '#B91C1C' }}>
          🗑 Delete Event{info.locked ? ' 🔒' : ''}
        </button>
      </div>
      {err ? <p className="text-sm font-bold mt-2" style={{ color: '#B91C1C' }}>{err}</p> : null}

      {/* Scored qualifying results — the PRIMARY list, rendered FIRST (right
          after the actions) so the admin immediately sees every team with its
          score, ready to Approve & Lock. The per-team QR grid (test-only, and
          104 cards tall on a full field) now lives BELOW it and collapsed, so
          it can never bury this list. */}
      <div className="mt-5">
        <h2 className="font-extrabold text-base mb-2" style={{ color: PRIMARY }}>
          Teams ({submittedCount} submitted / {teams.length} total)
        </h2>
        {teams.length === 0 ? (
          <p className="text-sm text-gray-600 italic">
            No teams yet. Share the QR — players check in and start their qualifying round from there.
          </p>
        ) : (
          <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${ACCENT}55` }}>
            <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] uppercase tracking-widest font-extrabold"
                 style={{ background: '#FFFFFF', color: PRIMARY }}>
              <div className="col-span-4">Team</div>
              <div className="col-span-4">Players</div>
              <div className="col-span-2 text-right">Score</div>
              <div className="col-span-1 text-right">Time</div>
              <div className="col-span-1 text-center">Approved</div>
            </div>
            {teams.map((t) => (
              <TeamRow key={t.id} eventId={eventId} team={t} closed={closed} />
            ))}
          </div>
        )}
      </div>

      {/* TEST: per-team qualify QR codes — below the scored list, collapsed by default. */}
      {PBCC_PREMIUM ? <TeamQrCodesSection eventId={eventId} origin={origin} teams={teams} teamsMap={teamsMap} /> : null}

      {showCreateChoice ? (
        <CreateTournamentChoiceModal
          eventId={eventId}
          info={info}
          submittedCount={submittedCount}
          teamsMap={teamsMap}
          onClose={() => setShowCreateChoice(false)}
        />
      ) : null}

      {/* Submit to Tournament → the field list + two build paths (AI / Manual).
          Both run the SAME deterministic build (onBuild = buildTournamentFromQualifying);
          "Build with AI" then opens the AI Assistant primed, "Build Manually" drops
          straight into the tournament admin. */}
      {showSubmitChoice ? (
        <TournamentCreatedRosterModal
          tournamentId={info.sourceRegistrationId}
          approvedTeams={teams
            .filter((t) => t.approved)
            .map((t) => ({ teamName: t.teamName, qualifyingScore: t.score, player1: t.player1, player2: t.player2 }))}
          tournamentName={info.name || ''}
          qualifyingEventName={info.name || ''}
          titleText="Submit to Tournament — Ready to Build"
          introText={<>Your qualifying field is approved and locked. Pick how to build the tournament from these teams.</>}
          onBuild={async () => {
            await buildTournamentFromQualifying(info.sourceRegistrationId, eventId, { isPractice: info.isPractice })
          }}
          onClose={() => setShowSubmitChoice(false)}
        />
      ) : null}

      {/* Delete access-code gate — in-app modal (not window.prompt). Deleting a
          qualifying event requires the 4-digit code 1996; Cancel or a wrong code
          deletes nothing. Protects the real 104-team event from a fat-finger. */}
      {deleteGate ? (
        <div
          onClick={() => setDeleteGate(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70, padding: 20 }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, padding: 22, width: '100%', maxWidth: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            <h3 style={{ fontSize: 18, fontWeight: 900, margin: 0, color: '#B91C1C' }}>Delete qualifying event?</h3>
            <p style={{ fontSize: 13, color: '#374151', margin: '8px 0 14px', lineHeight: 1.45 }}>
              This permanently deletes the entire event and cannot be undone. Enter the 4-digit access code to confirm.
            </p>
            <input
              autoFocus
              type="password"
              inputMode="numeric"
              maxLength={4}
              value={delCode}
              onChange={(e) => { setDelCode(e.target.value.replace(/\D/g, '')); setDelErr('') }}
              onKeyDown={(e) => { if (e.key === 'Enter') submitDeleteCode() }}
              placeholder="• • • •"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 24, letterSpacing: 8, textAlign: 'center', padding: '10px 12px', borderRadius: 10, border: `2px solid ${delErr ? '#B91C1C' : '#D1D5DB'}`, outline: 'none' }}
            />
            {delErr ? <p style={{ color: '#B91C1C', fontSize: 12, fontWeight: 800, margin: '8px 0 0' }}>{delErr}</p> : null}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="button" onClick={() => setDeleteGate(false)} className="btn-secondary text-sm" style={{ flex: 1 }}>Cancel</button>
              <button
                type="button"
                onClick={submitDeleteCode}
                disabled={delCode.length < 4}
                className="btn-primary text-sm"
                style={{ flex: 1, background: '#B91C1C', borderColor: '#B91C1C', opacity: delCode.length < 4 ? 0.5 : 1 }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </DetailShell>
  )
}

function TeamRow({ eventId, team, closed }) {
  const [expanded, setExpanded] = useState(false)
  const submitted = !!team.submittedAt
  const time = team.submittedAt ? new Date(team.submittedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''
  const onToggleApproved = async (e) => {
    const next = e.target.checked
    const { update, ref } = await import('firebase/database')
    const { db } = await import('../lib/firebase.js')
    await update(tref(`qualifyingEvents/${eventId}/teams/${team.id}`), { approved: next })
  }
  return (
    <>
      <div
        className="grid grid-cols-12 gap-2 px-3 py-2 text-sm items-center border-t"
        style={{ borderColor: 'rgba(var(--ge-primary-rgb),0.20)', background: '#FFFFFF' }}>
        <div className="col-span-4 font-extrabold flex items-center gap-2" style={{ color: PRIMARY }}>
          <span className="truncate">{team.teamName}</span>
          <button
            type="button"
            onClick={() => setExpanded((x) => !x)}
            className="text-[10px] font-extrabold uppercase tracking-widest rounded-md px-2 py-0.5 shrink-0"
            style={{
              // Dark green text on a solid white button with a dark border —
              // legible on every build. (CLUB.accentColor is WHITE on the
              // FOURBALL_ONLY builds, so `color: ACCENT` rendered white-on-white.)
              color: 'var(--ge-primary)',
              background: '#FFFFFF',
              border: '1.5px solid var(--ge-primary)',
            }}
            title={expanded ? 'Hide scorecard' : 'View scorecard'}>
            {expanded ? '▲ Hide' : '▼ View Scorecard'}
          </button>
        </div>
        <div className="col-span-4 text-xs" style={{ color: '#1F2937' }}>
          {[team.player1, team.player2].filter(Boolean).join(' · ') || <span style={{ color: '#4B5563' }}>—</span>}
        </div>
        <div className="col-span-2 text-right font-mono font-bold" style={{ color: submitted ? PRIMARY : '#1F2937' }}>
          {submitted ? team.score : <span className="italic">pending</span>}
        </div>
        <div className="col-span-1 text-right text-[11px]" style={{ color: '#374151' }}>{time}</div>
        <div className="col-span-1 text-center">
          <input
            type="checkbox"
            checked={!!team.approved}
            onChange={onToggleApproved}
            disabled={closed}
            aria-label={`Approve ${team.teamName}`}
          />
        </div>
      </div>
      {expanded ? (
        <ScorecardStrip team={team} eventId={eventId} />
      ) : null}
    </>
  )
}

// Read-only hole-by-hole grid. Same shape the player sees on the public
// /qualify page during scoring — 9 columns per row, two rows for OUT/IN.
// Holes that haven't been scored render an em-dash so the admin can see
// at a glance which holes are still pending.
//
// Cell color follows score-vs-par:
//   ≤ -2 (eagle+) → blue
//      -1 (birdie) → red
//       0 (par)    → unchanged (default white card)
//      +1 (bogey)  → yellow
//   ≥ +2 (double+) → orange
// Standalone qualifying events aren't tied to a tournament so par per
// hole comes from DEFAULT_PARS (CLUB.course.pars), same fallback the
// public /qualify scorecard uses when info.pars isn't set.
function scoreColor(diff) {
  // Bold solid fills; the hole NUMBER is always BLACK (never white), every cell.
  if (diff <= -2) return { bg: '#2563EB', text: '#111827', border: '#1D4ED8' } // eagle or better — blue
  if (diff === -1) return { bg: '#DC2626', text: '#111827', border: '#B91C1C' } // birdie — red
  if (diff === 1)  return { bg: '#EAB308', text: '#111827', border: '#CA8A04' } // bogey — yellow
  if (diff >= 2)   return { bg: '#F97316', text: '#111827', border: '#EA580C' } // double or worse — orange
  return null // par — white bg, black number
}

// EDITABLE manual-entry scorecard. Staff type hole-by-hole (which auto-sums the
// total) and/or a total directly, then Save — which posts score + holes +
// submittedAt to the team via submitQualifyingEventResult, so the SCORE column
// updates live. Every field is dark-on-white and fully editable (not read-only).
function ScorecardStrip({ team, eventId }) {
  const initHoles = () => {
    const src = team?.holes || {}
    const out = {}
    for (let h = 1; h <= 18; h++) {
      const raw = src[h] ?? src[String(h)]
      const n = Number(raw)
      out[h] = Number.isFinite(n) && n > 0 ? String(n) : ''
    }
    return out
  }
  const [holes, setHoles] = useState(initHoles)
  const [total, setTotal] = useState(team?.score != null && Number(team.score) > 0 ? String(team.score) : '')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  const sumHoles = useMemo(() => {
    let s = 0, any = false
    for (let h = 1; h <= 18; h++) { const n = Number(holes[h]); if (Number.isFinite(n) && n > 0) { s += n; any = true } }
    return any ? s : null
  }, [holes])

  // Editing a hole auto-fills the total from the running sum (staff entering
  // hole-by-hole never have to add it up). They can still type the total
  // directly when they only know the final number.
  const setHole = (h, v) => {
    const clean = v.replace(/[^0-9]/g, '').slice(0, 2)
    setHoles((prev) => {
      const next = { ...prev, [h]: clean }
      let s = 0, any = false
      for (let i = 1; i <= 18; i++) { const n = Number(next[i]); if (Number.isFinite(n) && n > 0) { s += n; any = true } }
      if (any) setTotal(String(s))
      return next
    })
  }

  const save = async () => {
    const scoreNum = Number(total)
    if (!Number.isFinite(scoreNum) || scoreNum <= 0) { setMsg('Enter a total (or hole scores) first.'); return }
    setBusy(true); setMsg('')
    try {
      const holesOut = {}
      for (let h = 1; h <= 18; h++) { const n = Number(holes[h]); if (Number.isFinite(n) && n > 0) holesOut[h] = n }
      await submitQualifyingEventResult(eventId, team.id, { score: scoreNum, holes: holesOut, teamName: team.teamName })
      setMsg('✓ Saved — score posted.')
    } catch (e) {
      setMsg(e?.message || 'Save failed.')
    }
    setBusy(false)
  }

  return (
    <div
      className="border-t px-3 py-3"
      style={{ borderColor: 'rgba(var(--ge-primary-rgb),0.20)', background: '#FFFFFF' }}>
      <p className="text-[10px] uppercase tracking-widest font-extrabold mb-2" style={{ color: PRIMARY }}>
        Enter Scorecard · {team.teamName}
      </p>
      {/* One-line color key — same palette as the hole cells. */}
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-bold mb-3"
        style={{ color: PRIMARY }}>
        <LegendChip color="#1D4ED8" bg="#2563EB" border="#1D4ED8" label="Eagle or better" />
        <LegendChip color="#B91C1C" bg="#DC2626" border="#B91C1C" label="Birdie" />
        <LegendChip color={PRIMARY} bg="#FFFFFF" border={`${PRIMARY}55`} label="Par" />
        <LegendChip color="#A16207" bg="#EAB308" border="#CA8A04" label="Bogey" />
        <LegendChip color="#C2410C" bg="#F97316" border="#EA580C" label="Double or worse" />
      </div>
      <div className="grid grid-cols-9 gap-1">
        {Array.from({ length: 18 }, (_, i) => i + 1).map((h) => {
          const par = Number(DEFAULT_PARS[h - 1]) || 4
          const n = Number(holes[h])
          const has = Number.isFinite(n) && n > 0
          const color = has ? scoreColor(n - par) : null
          const bg = color?.bg || '#FFFFFF'
          const border = color?.border || `${PRIMARY}40`
          const text = color?.text || '#111827' // par/empty — black number on white
          return (
            <div
              key={h}
              className="rounded-md py-1 text-center"
              style={{ background: bg, border: `1px solid ${border}` }}>
              <div className="text-xs font-black" style={{ color: '#111827' }}>H{h}</div>
              <input
                type="text"
                inputMode="numeric"
                value={holes[h]}
                onChange={(e) => setHole(h, e.target.value)}
                placeholder="–"
                aria-label={`Hole ${h} score`}
                className="w-full text-center text-lg font-black font-mono bg-transparent outline-none"
                style={{ color: text }}
              />
            </div>
          )
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col">
          <span className="text-[10px] uppercase tracking-widest font-extrabold mb-1" style={{ color: PRIMARY }}>Total score</span>
          <input
            type="text"
            inputMode="numeric"
            value={total}
            onChange={(e) => setTotal(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
            placeholder="e.g. 72"
            aria-label="Total score"
            className="rounded-lg px-3 py-2 text-lg font-extrabold font-mono w-28 outline-none"
            style={{ color: PRIMARY, background: '#FFFFFF', border: `2px solid ${PRIMARY}` }}
          />
        </label>
        {sumHoles != null ? (
          <button
            type="button"
            onClick={() => setTotal(String(sumHoles))}
            className="text-[11px] font-bold rounded-md px-2 py-1"
            style={{ color: PRIMARY, background: '#FFFFFF', border: `1px solid ${PRIMARY}` }}
            title="Set total to the sum of the holes entered">
            = sum of holes ({sumHoles})
          </button>
        ) : null}
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded-lg px-4 py-2 text-sm font-extrabold uppercase tracking-widest disabled:opacity-60"
          style={{ color: '#FFFFFF', background: PRIMARY, border: `2px solid ${PRIMARY}` }}>
          {busy ? 'Saving…' : (team.submittedAt ? '💾 Update Score' : '💾 Save Score')}
        </button>
        {msg ? <span className="text-sm font-bold" style={{ color: msg.startsWith('✓') ? '#15803D' : '#B91C1C' }}>{msg}</span> : null}
      </div>
      {team.submittedAt ? (
        <p className="text-[11px] mt-2 font-semibold" style={{ color: PRIMARY }}>
          Posted total {team.score} · last submitted {new Date(team.submittedAt).toLocaleString()}
        </p>
      ) : (
        <p className="text-[11px] italic mt-2" style={{ color: '#374151' }}>
          No score posted yet — type the holes and/or a total, then Save.
        </p>
      )}
    </div>
  )
}

// Color-key chip used by ScorecardStrip's legend row. Same palette the
// cells below it use so the eye maps from chip → grid one-to-one.
function LegendChip({ color, bg, border, label }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 18, height: 18, borderRadius: 4,
          background: bg,
          border: `1.5px solid ${border}`,
        }}
      />
      <span style={{ color }}>{label}</span>
    </span>
  )
}

// "Create Tournament from This List" — two-step flow:
//
//   STEP 1 (form): admin fills name/date/format/course AND picks a
//   qualifying event from a "Qualifying List" dropdown (defaults to the
//   one they came from). Submit creates the tournament, reads the
//   selected event's approved teams, and writes them into
//   /tournaments/{newId}/registrations via bulkAddRegistrations.
//
//   STEP 2 (review): same modal shell, new content — title flips to
//   "Tournament Created — Ready to Build" and shows a numbered roster
//   of the approved teams sorted by qualifying score. Two buttons:
//   SET UP WITH AI dispatches the global ai-prefill window event after
//   navigation so the floating ✨ AI Assistant on the tournament page
//   opens with a primed prompt; SET UP MANUALLY just navigates.
function CreateTournamentChoiceModal({ eventId, info, submittedCount, teamsMap, onClose }) {
  const navigate = useNavigate()

  // Subscribe to every qualifying event for the dropdown. The list shows
  // each event's name + count of approved teams so the admin can pick
  // the right one at a glance.
  const [allEvents, setAllEvents] = useState({})
  useEffect(() => {
    const off = watchQualifyingEvents(setAllEvents)
    return () => off()
  }, [])

  const eventOptions = useMemo(() => {
    return Object.entries(allEvents || {}).map(([id, v]) => {
      const teams = v?.teams || {}
      const approved = Object.values(teams).filter((t) => t && t.approved === true).length
      return {
        id,
        name: v?.info?.name || '(unnamed)',
        approved,
      }
    }).sort((a, b) => a.name.localeCompare(b.name))
  }, [allEvents])

  // Form state — pre-fill from the qualifying event the admin came from.
  // selectedEventId is the live source for the approved-team read at
  // submit time, so changing the dropdown changes which event's teams
  // get carried into the new tournament.
  const today = new Date().toISOString().slice(0, 10)
  const [selectedEventId, setSelectedEventId] = useState(eventId)
  const [name, setName] = useState(info?.name || 'Tournament')
  const [course, setCourse] = useState(CLUB.course?.name || '')
  const [date, setDate] = useState(today)
  const [format, setFormat] = useState(info?.format || QE_FORMATS[0])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [progress, setProgress] = useState('')

  // Step 2 state — populated after the create succeeds. tournamentId
  // drives the SET UP buttons' navigation target.
  const [step, setStep] = useState('form') // 'form' | 'created'
  const [createdTournamentId, setCreatedTournamentId] = useState(null)
  const [createdApprovedTeams, setCreatedApprovedTeams] = useState([])

  // Approved count for the currently selected event (drives the button
  // label + warning copy). When the dropdown changes, this updates.
  const selectedApprovedCount = useMemo(() => {
    const opt = eventOptions.find((o) => o.id === selectedEventId)
    return opt ? opt.approved : 0
  }, [eventOptions, selectedEventId])

  const submit = async () => {
    setBusy(true); setErr(''); setProgress('Creating tournament…')
    try {
      // Step 1: create the tournament.
      const tournamentId = await createTournament({
        name: name.trim(),
        course: course.trim(),
        date,
        format,
      })
      console.log('[QE→Tournament] created tournament', tournamentId)

      // Step 2: re-read approved teams from the SELECTED qualifying
      // event (not necessarily the eventId prop — the dropdown can
      // point at a different one).
      setProgress('Reading approved qualifying teams…')
      const snap = await get(tref(`qualifyingEvents/${selectedEventId}/teams`))
      const map = snap.val() || {}
      const approved = Object.values(map).filter((t) => t && t.approved === true)
      console.log('[QE→Tournament] approved teams read from Firebase:', approved.length, approved)

      // Step 3: map to registration rows + bulk insert. Strip
      // undefined fields so Firebase doesn't reject them.
      const rows = approved.map((t) => {
        const teamName = String(t.teamName || '').trim() || 'Team'
        const players = [t.player1, t.player2]
          .filter((p) => p && String(p).trim().length > 0)
          .map((p) => String(p).trim())
        const qs = Number.isFinite(Number(t.qualifyingScore))
          ? Number(t.qualifyingScore)
          : (Number.isFinite(Number(t.score)) ? Number(t.score) : null)
        const row = { teamName, qualifyingStatus: 'complete' }
        if (players.length > 0) row.players = players
        if (qs != null) row.qualifyingScore = qs
        return row
      })
      console.log('[QE→Tournament] registration rows to write:', rows.length, rows.slice(0, 3))

      if (rows.length > 0) {
        setProgress(`Adding ${rows.length} teams to tournament…`)
        const written = await bulkAddRegistrations(tournamentId, rows)
        console.log('[QE→Tournament] bulkAddRegistrations returned:', written)
      }

      // Step 4: verify the write landed.
      setProgress('Verifying teams were written…')
      const regSnap = await get(tref(`tournaments/${tournamentId}/registrations`))
      const regMap = regSnap.val() || {}
      const regCount = Object.keys(regMap).length
      console.log('[QE→Tournament] verification read /registrations count:', regCount, regMap)
      if (rows.length > 0 && regCount === 0) {
        throw new Error(`Wrote ${rows.length} rows but verification read found 0 registrations. Tournament ID ${tournamentId}.`)
      }

      // Step 5: hand off to STEP 2. Stash the team list (sorted lowest
      // score first — best qualifier on top) for the roster modal and
      // flip the step.
      const sorted = [...approved].sort((a, b) => {
        const sa = Number(a.qualifyingScore ?? a.score ?? 0)
        const sb = Number(b.qualifyingScore ?? b.score ?? 0)
        return sa - sb
      })
      setCreatedTournamentId(tournamentId)
      setCreatedApprovedTeams(sorted)
      setStep('created')
      setBusy(false)
      setProgress('')
    } catch (e) {
      console.error('[QE→Tournament] failed:', e)
      setErr(e?.message || 'Create failed.')
      setBusy(false)
      setProgress('')
    }
  }

  const ready = name.trim().length > 0 && course.trim().length > 0 && date.length > 0 && selectedEventId

  if (step === 'created') {
    // Look up the selected qualifying event's display name (the
    // dropdown value may have changed between mount and submit).
    const qen = allEvents?.[selectedEventId]?.info?.name || ''
    return (
      <TournamentCreatedRosterModal
        tournamentId={createdTournamentId}
        approvedTeams={createdApprovedTeams}
        tournamentName={name.trim()}
        qualifyingEventName={qen}
        onClose={onClose}
      />
    )
  }

  // STEP 1: the create form (now with Qualifying List dropdown)
  return (
    <Modal title="Create Tournament from List" onClose={busy ? () => {} : onClose}>
      <p className="text-sm" style={{ color: PRIMARY }}>
        Build a new tournament and carry over <b>{selectedApprovedCount}</b> approved {selectedApprovedCount === 1 ? 'team' : 'teams'} from the selected qualifying list.
      </p>
      {selectedApprovedCount === 0 ? (
        <p className="text-xs mt-2" style={{ color: '#B45309' }}>
          ⚠ No teams have been approved on the selected qualifying list. Approve some first, or this will create an empty tournament.
        </p>
      ) : null}

      <div className="space-y-3 mt-4">
        <Field label="Qualifying List">
          <select
            value={selectedEventId || ''}
            onChange={(e) => setSelectedEventId(e.target.value)}
            className="input"
            disabled={busy}>
            {eventOptions.length === 0 ? (
              <option value="">No qualifying events found</option>
            ) : null}
            {eventOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} — {o.approved} approved
              </option>
            ))}
          </select>
        </Field>
        <Field label="Tournament Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            disabled={busy}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input"
              disabled={busy}
            />
          </Field>
          <Field label="Format">
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="input"
              disabled={busy}>
              {QE_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Course">
          <input
            type="text"
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            className="input"
            disabled={busy}
          />
        </Field>
      </div>

      {progress ? <p className="text-xs mt-2" style={{ color: PRIMARY }}>{progress}</p> : null}
      {err ? <p className="text-sm font-bold mt-2" style={{ color: '#B91C1C' }}>{err}</p> : null}

      <div className="flex gap-2 justify-end mt-4">
        <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={submit} disabled={busy || !ready}>
          {busy ? 'Working…' : `Create Tournament${selectedApprovedCount > 0 ? ` (${selectedApprovedCount} ${selectedApprovedCount === 1 ? 'team' : 'teams'})` : ''}`}
        </button>
      </div>
    </Modal>
  )
}

// Shared shell — Back + Home buttons up top, white card body. Used by
// both the loaded detail view and the loading/not-found fallbacks so
// every state still gives the admin a way out.
function DetailShell({ children }) {
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--ge-primary)' }}>
      {/* One subtle back-into-the-flow link — this is the next step of the PBCC
          4-Ball flow, not a standalone page, so no Back-to-Admin/Home chrome.
          Moving forward happens via "Submit to Tournament" (rolls to State 3). */}
      <header className="px-4 py-3">
        <Link
          to="/admin/pbcc-4ball"
          className="text-xs font-extrabold uppercase tracking-widest"
          style={{ color: ACCENT, textDecoration: 'none' }}>
          ← PBCC 4-Ball
        </Link>
      </header>
      <main className="max-w-5xl mx-auto p-4 sm:p-6">
        <div
          className="rounded-2xl p-5 sm:p-6"
          style={{ background: '#FFFFFF', border: `1px solid ${ACCENT}30` }}>
          {children}
        </div>
      </main>
      <AIAssistant tournamentId={null} />
    </div>
  )
}

// Step-2 modal — exported so AdminApp.jsx's CreateModal flow can reuse
// the same roster + setup-path UI after it imports approved teams from
// a qualifying event. Self-contained: takes a tournamentId and the
// approved-teams array, renders the numbered roster sorted by
// qualifying score, and handles the two SET UP buttons.
//
// tournamentName + qualifyingEventName are stitched into the
// ai-prefill payload so the AI Assistant gets full context ("I just
// created PBCC 4-Ball 2026 from the PBCC 4-Ball Qualifier …") instead
// of a bare "set up this tournament" command.
export function TournamentCreatedRosterModal({
  tournamentId,
  approvedTeams = [],
  tournamentName = '',
  qualifyingEventName = '',
  onClose,
  onBuild = null,        // optional async: run BEFORE navigating (e.g. promote the node)
  titleText = 'Tournament Created — Ready to Build',
  introText = null,
}) {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)

  // Defensive sort — caller usually pre-sorts, but the roster modal
  // should never trust input order. Lowest qualifying score first.
  const sorted = useMemo(() => {
    return [...(approvedTeams || [])].sort((a, b) => {
      const sa = Number(a?.qualifyingScore ?? a?.score ?? 0)
      const sb = Number(b?.qualifyingScore ?? b?.score ?? 0)
      return sa - sb
    })
  }, [approvedTeams])

  // When onBuild is provided (the New-Tournament promote flow), run it first —
  // that promotes the same node into the live tournament — then proceed to the
  // AI / manual setup exactly as before. Returns false if the build failed.
  const runBuild = async () => {
    if (!onBuild) return true
    setBusy(true)
    try { await onBuild(); return true }
    catch (e) { setBusy(false); window.alert(e?.message || 'Build failed.'); return false }
  }

  const onSetUpManually = async () => {
    if (!(await runBuild())) return
    onClose()
    // Hard redirect — same rationale as Set Up with AI below: forces a
    // clean React tree rebuild so AdminApp mounts TournamentRoute from
    // scratch instead of relying on in-place route swap.
    window.location.href = `/admin/t/${tournamentId}`
  }
  const onSetUpWithAI = async () => {
    if (!(await runBuild())) return
    // onBuild flow (the PBCC Submit-to-Tournament / promote path): the
    // deterministic build ALREADY ran in onBuild, so the tournament is fully
    // built. Land on it with the AI assistant CLOSED — no auto-opened chat, no
    // pre-filled prompt. The admin opens the assistant themselves if they want
    // to make adjustments. Hard redirect so AdminApp remounts cleanly.
    if (onBuild) {
      onClose()
      window.location.href = `/admin/t/${tournamentId}`
      return
    }
    // Legacy flow (no onBuild): the tournament exists but its flights are NOT
    // built yet — here the AI itself does the build, so it still needs the
    // primed auto-send command on the destination screen.
    const teamCount = sorted.length
    const tn = tournamentName?.trim() || 'this tournament'
    const qen = qualifyingEventName?.trim() || 'the selected qualifying list'
    const prefill = {
      command:
        `Tournament already created. Tournament ID: ${tournamentId}. ` +
        `Tournament name: ${tn}. ` +
        `Imported from ${qen} qualifying list with ${teamCount} teams sorted by qualifying score. ` +
        `Do not create a new tournament. ` +
        `Please build the flights and set up the full tournament now.`,
      autoSend: true,
    }
    try {
      sessionStorage.setItem('ai-prefill', JSON.stringify(prefill))
    } catch {}
    onClose()
    window.location.href = `/admin/t/${tournamentId}`
  }

  return (
    <Modal title={titleText} onClose={onClose}>
      <p className="text-sm" style={{ color: PRIMARY }}>
        {introText || <>Your tournament was created with <b>{sorted.length}</b> approved {sorted.length === 1 ? 'team' : 'teams'} from the qualifying list. Pick a setup path below.</>}
      </p>

      <div
        className="mt-4 rounded-xl p-3"
        style={{ background: '#FFFFFF', border: `1px solid ${ACCENT}55`, maxHeight: 320, overflowY: 'auto' }}>
        <p className="text-[10px] uppercase tracking-widest font-extrabold mb-2" style={{ color: ACCENT }}>
          Field — {sorted.length} team{sorted.length === 1 ? '' : 's'} (sorted by qualifying score)
        </p>
        <ol className="text-sm space-y-1 list-decimal list-inside" style={{ color: PRIMARY }}>
          {sorted.map((t, i) => {
            const score = Number(t?.qualifyingScore ?? t?.score ?? null)
            return (
              <li key={`${t?.teamName || 'team'}-${i}`}>
                <span className="font-bold">{t?.teamName || 'Team'}</span>
                {Number.isFinite(score) ? (
                  <span className="font-mono" style={{ color: 'rgba(var(--ge-primary-rgb),0.65)' }}> · {score}</span>
                ) : null}
              </li>
            )
          })}
        </ol>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={onSetUpWithAI}
          disabled={busy}
          className="rounded-lg py-3 px-4 font-extrabold text-sm uppercase tracking-widest transition active:scale-[0.98] disabled:opacity-60"
          style={{ background: PRIMARY, color: ACCENT, border: `1.5px solid ${ACCENT}` }}>
          {busy ? 'Building…' : '✦ Build with AI'}
        </button>
        <button
          type="button"
          onClick={onSetUpManually}
          disabled={busy}
          className="rounded-lg py-3 px-4 font-extrabold text-sm uppercase tracking-widest transition active:scale-[0.98] disabled:opacity-60"
          style={{ background: ACCENT, color: 'var(--ge-primary)', border: '1.5px solid var(--ge-primary)' }}>
          {busy ? 'Building…' : '🔧 Build Manually'}
        </button>
      </div>
      <p className="text-[11px] text-gray-500 mt-3 leading-snug">
        AI: opens the floating ✦ AI Assistant on the new tournament page with a primed prompt.
        Manual: jumps straight into the tournament admin so you can configure flights, tee times, and brackets yourself.
      </p>
    </Modal>
  )
}
